// src/components/admin/CategorieTitreForm.jsx
import React, { useState, useEffect, useCallback } from 'react';
import '../../styles/admin/categorieTitreForm.css';
import { asset } from '../../services/api';
import {
  getCategories,
  addCategorieWithImage,
  updateCategorie as apiUpdateCategorie,
  deleteCategorie as apiDeleteCategorie,
  getTitresByCategorie,
  addTitre as apiAddTitre,
  deleteTitre as apiDeleteTitre,
  getProduitsParCategorie,
  getProduitsParTitre,
} from '../../services/categorieTitreService';

function CategorieTitreForm({ onUpdate }) {
  const [categorie, setCategorie] = useState('');
  const [image, setImage] = useState(null);
  const [titre, setTitre] = useState('');
  const [selectedCategorie, setSelectedCategorie] = useState('');
  const [categories, setCategories] = useState([]);
  const [editingCategorieId, setEditingCategorieId] = useState(null);
  const [titresParCategorie, setTitresParCategorie] = useState([]);
  const [nomCategorieSelectionnee, setNomCategorieSelectionnee] = useState('');
  const [produitsParCategorie, setProduitsParCategorie] = useState(null);
  const [produitsParTitre, setProduitsParTitre] = useState(null);
  const [categorieActiveId, setCategorieActiveId] = useState(null);

  const loadCategories = useCallback(async () => {
    try {
      const data = await getCategories();
      setCategories(data);
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Erreur chargement catégories :', err);
    }
  }, [onUpdate]);

  useEffect(() => { loadCategories(); }, [loadCategories]);

  const loadTitres = async (categorieId) => {
    try {
      const data = await getTitresByCategorie(categorieId);
      setTitresParCategorie(data);
      const selectedCat = categories.find(cat => cat.id === parseInt(categorieId));
      if (selectedCat) setNomCategorieSelectionnee(selectedCat.categorie);
      fetchProduitsCategorie(categorieId);
    } catch (err) {
      console.error('Erreur chargement titres :', err);
    }
  };

  const fetchProduitsCategorie = async (id) => {
    try {
      const data = await getProduitsParCategorie(id);
      setProduitsParCategorie(data.total);
    } catch (err) {
      console.error('Erreur chargement produits par catégorie :', err);
    }
  };

  const fetchProduitsTitre = async (id) => {
    try {
      const data = await getProduitsParTitre(id);
      setProduitsParTitre(data.total);
    } catch (err) {
      console.error('Erreur chargement produits par titre :', err);
    }
  };

  const handleAfficherTitres = async (categorieId) => {
    if (categorieActiveId === categorieId) {
      setCategorieActiveId(null);
      setTitresParCategorie([]);
    } else {
      await loadTitres(categorieId);
      setCategorieActiveId(categorieId);
    }
  };

  const addCategorie = async () => {
    if (!categorie) return;
    try {
      await addCategorieWithImage(categorie, image);
      setCategorie('');
      setImage(null);
      loadCategories();
    } catch (err) {
      console.error('Erreur ajout catégorie :', err);
      alert(err.message);
    }
  };

  const updateCategorie = async () => {
    if (!categorie || !editingCategorieId) return;
    await apiUpdateCategorie(editingCategorieId, { categorie });
    setCategorie('');
    setEditingCategorieId(null);
    loadCategories();
  };

  const handleDeleteCategorie = async (id) => {
    if (!window.confirm('Supprimer cette catégorie ?')) return;
    await apiDeleteCategorie(id);
    loadCategories();
  };

  const handleDeleteTitre = async (id) => {
  if (!window.confirm("Supprimer ce titre ?")) return;
  try {
    await apiDeleteTitre(id);
    setTitresParCategorie((prev) => prev.filter((t) => t.id !== id));
  } catch (err) {
    console.error("Erreur suppression titre :", err);
    alert("Suppression impossible.");
  }
};

  const handleAddTitre = async () => {
    if (!selectedCategorie || !titre) {
      alert('Remplir la catégorie et le titre !');
      return;
    }
    await apiAddTitre({ title: titre, categorie_id: selectedCategorie });
    setTitre('');
    setSelectedCategorie('');
    loadTitres(selectedCategorie);
  };

  return (
    <div className="categorie-titre-form">
      <div className="ct-grid">
        {/* Colonne gauche : formulaires (élargie) */}
        <div className="ct-left">
          <div className="form-box">
            <h3>Ajouter une Catégorie</h3>
            <input
              type="text"
              placeholder="Nom de la catégorie"
              value={categorie}
              onChange={(e) => setCategorie(e.target.value)}
            />
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setImage(e.target.files[0])}
            />
            {editingCategorieId ? (
              <>
                <button onClick={updateCategorie}>✅ Modifier</button>
                <button onClick={() => { setEditingCategorieId(null); setCategorie(''); }}>❌ Annuler</button>
              </>
            ) : (
              <button onClick={addCategorie}>Ajouter Catégorie</button>
            )}
          </div>

          <div className="form-box">
            <h3>Ajouter un Titre à une Catégorie</h3>
            <select
              value={selectedCategorie}
              onChange={(e) => {
                setSelectedCategorie(e.target.value);
                loadTitres(e.target.value);
              }}
            >
              <option value="">Choisir catégorie</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>{cat.categorie}</option>
              ))}
            </select>

            <input
              type="text"
              placeholder="Nouveau titre"
              value={titre}
              onChange={(e) => setTitre(e.target.value)}
            />
            <button onClick={handleAddTitre}>Ajouter Titre</button>
          </div>
        </div>

        {/* Colonne droite : tableau */}
        <div className="ct-right">
          <h3>Liste des Catégories</h3>
          <div className="table-wrap">
            <table className="categories-table">
              <colgroup>
                <col style={{ width: '64px' }} />
                <col style={{ width: '110px' }} />
                <col /> {/* Catégorie */}
                <col className="actions-col" /> {/* Actions étroite */}
              </colgroup>

              <thead>
                <tr>
                  <th>#</th>
                  <th>Image</th>
                  <th>Catégorie</th>
                  <th className="actions">Actions</th>
                </tr>
              </thead>

              <tbody>
                {categories.map((cat, index) => (
                  <React.Fragment key={cat.id}>
                    <tr>
                      <td>{index + 1}</td>
                      <td>
                        {cat.image ? (
                          <img
                            src={asset(cat.image)}                            
                            alt={cat.categorie}
                            style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 8 }}
                          />
                        ) : (
                          <span style={{ fontStyle: 'italic', color: '#999' }}>Aucune</span>
                        )}
                      </td>
                      <td>{cat.categorie}</td>
                      <td className="actions">
                        <div className="actions-btns">
                          <button className="btn-icon" title="Modifier"
                            onClick={() => { setEditingCategorieId(cat.id); setCategorie(cat.categorie); }}>✏️</button>
                          <button className="btn-icon" title="Supprimer"
                            onClick={() => handleDeleteCategorie(cat.id)}>🗑️</button>
                          <button className="btn-icon" title="Titres"
                            onClick={() => handleAfficherTitres(cat.id)}>📂</button>
                        </div>
                      </td>
                    </tr>

                    {categorieActiveId === cat.id && titresParCategorie.length > 0 && (
                      <tr>
                        {/* ⚠️ 4 colonnes -> colSpan=4 */}
                        <td colSpan="4">
                          <ul className="titre-list-inline">
                            {titresParCategorie.map((t) => (
                              <li key={t.id}>
                                {t.title}
                                <span>
                                  <button className="btn-chip" onClick={() => handleDeleteTitre(t.id)}>🗑️</button>
                                  <button className="btn-chip" onClick={() => fetchProduitsTitre(t.id)}>📊</button>
                                </span>
                              </li>
                            ))}
                          </ul>

                          {produitsParTitre !== null && (
                            <p style={{ fontStyle: 'italic' }}>
                              Produits pour ce titre : <strong>{produitsParTitre}</strong>
                            </p>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {titresParCategorie.length > 0 && (
            <>
              <h4>
                Titres liés à : <span style={{ color: 'green' }}>{nomCategorieSelectionnee}</span>
              </h4>
              {produitsParCategorie !== null && (
                <p style={{ fontStyle: 'italic' }}>
                  Produits dans cette catégorie : <strong>{produitsParCategorie}</strong>
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default CategorieTitreForm;
